import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2Icon, CheckIcon, FileTextIcon, Trash2Icon, UserIcon, ChevronDownIcon, MailCheckIcon, MinusCircleIcon, UploadIcon, ClipboardPenIcon, MessageSquareIcon, XIcon, HistoryIcon, ArrowRightIcon, PlusCircleIcon, LogOutIcon, ArrowRightLeftIcon, PencilIcon, GlobeIcon, LockIcon, SendIcon } from 'lucide-react';
import { Condition, Document, ConditionStatus, ConditionHistoryEntry, CommentVisibility } from '../types';
interface ConditionRowProps {
  condition: Condition;
  linkedDocument?: Document;
  isSelected?: boolean;
  groupName?: string;
  onToggleSelect?: () => void;
  onStatusChange: (status: ConditionStatus) => void;
  onRemoveFromGroup?: () => void;
  onRemove: () => void;
  onAddComment?: (conditionId: string, text: string, visibility: CommentVisibility) => void;
}
const formatDate = (date?: Date) => {
  if (!date) return '-';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};
const STATUS_CONFIG: Record<ConditionStatus, {
  label: string;
  bg: string;
  text: string;
  border: string;
  dot: string;
}> = {
  new: {
    label: 'New',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
    dot: 'bg-blue-500'
  },
  requested: {
    label: 'Requested',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
    dot: 'bg-amber-500'
  },
  review: {
    label: 'Review',
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    border: 'border-purple-200',
    dot: 'bg-purple-500'
  },
  cleared: {
    label: 'Cleared',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200',
    dot: 'bg-emerald-500'
  }
};
const ALL_STATUSES: ConditionStatus[] = ['new', 'requested', 'review', 'cleared'];
export function ConditionRow({
  condition,
  linkedDocument,
  isSelected,
  groupName,
  onToggleSelect,
  onStatusChange,
  onRemoveFromGroup,
  onRemove,
  onAddComment
}: ConditionRowProps) {
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [commentOpen, setCommentOpen] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentVisibility, setCommentVisibility] = useState<CommentVisibility>('portal');
  const commentInputRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setStatusDropdownOpen(false);
      }
    };
    if (statusDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Also close on Escape
      const handleKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') setStatusDropdownOpen(false);
      };
      document.addEventListener('keydown', handleKey);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleKey);
      };
    }
  }, [statusDropdownOpen]);
  useEffect(() => {
    if (commentOpen) {
      setTimeout(() => commentInputRef.current?.focus(), 100);
    }
  }, [commentOpen]);
  const handleSendComment = () => {
    if (commentText.trim() && onAddComment) {
      onAddComment(condition.id, commentText.trim(), commentVisibility);
      setCommentText('');
      // Keep panel open to see the comment appear
    }
  };
  const currentStatus = STATUS_CONFIG[condition.status];
  const history = condition.history ?? [];
  const comments = condition.comments ?? [];
  const sortedHistory = [...history].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  const getEventIcon = (type: ConditionHistoryEntry['type']) => {
    switch (type) {
      case 'created':
        return <PlusCircleIcon className="w-3 h-3" data-id="element-115" />;
      case 'status_change':
        return <ArrowRightIcon className="w-3 h-3" data-id="element-116" />;
      case 'group_added':
        return <MessageSquareIcon className="w-3 h-3" data-id="element-117" />;
      case 'group_removed':
        return <LogOutIcon className="w-3 h-3" data-id="element-118" />;
      case 'group_moved':
        return <ArrowRightLeftIcon className="w-3 h-3" data-id="element-119" />;
      case 'note_edited':
        return <PencilIcon className="w-3 h-3" data-id="element-120" />;
      case 'comment_added':
        return <MessageSquareIcon className="w-3 h-3" data-id="element-121" />;
    }
  };
  const getEventColor = (type: ConditionHistoryEntry['type']) => {
    switch (type) {
      case 'created':
        return {
          bg: '#dbeafe',
          text: '#1e40af'
        };
      case 'status_change':
        return {
          bg: '#fef3c7',
          text: '#92400e'
        };
      case 'group_added':
        return {
          bg: '#d1fae5',
          text: '#065f46'
        };
      case 'group_removed':
        return {
          bg: '#fee2e2',
          text: '#991b1b'
        };
      case 'group_moved':
        return {
          bg: '#e0e7ff',
          text: '#3730a3'
        };
      case 'note_edited':
        return {
          bg: '#fef9c3',
          text: '#854d0e'
        };
      case 'comment_added':
        return {
          bg: '#f0fdf4',
          text: '#166534'
        };
    }
  };
  const getEventLabel = (entry: ConditionHistoryEntry) => {
    switch (entry.type) {
      case 'created':
        return 'Condition created';
      case 'status_change':
        return `Status: ${STATUS_CONFIG[entry.fromStatus!]?.label} → ${STATUS_CONFIG[entry.toStatus!]?.label}`;
      case 'group_added':
        return `Added to group "${entry.toGroupName}"`;
      case 'group_removed':
        return `Removed from group "${entry.fromGroupName}"`;
      case 'group_moved':
        return `Moved from "${entry.fromGroupName}" → "${entry.toGroupName}"`;
      case 'note_edited':
        return `Group note edited: "${entry.fromNote}" → "${entry.toNote}"`;
      case 'comment_added':
        return `Comment (${entry.commentVisibility}): "${entry.commentText}"`;
    }
  };
  const formatHistoryDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };
  return <div data-id="element-122">
      <motion.div initial={{
      opacity: 0,
      y: -4
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -4
    }} className={`group flex items-center py-3 px-4 border-b border-slate-100 transition-colors ${isSelected ? 'bg-blue-50/50' : 'bg-white hover:bg-slate-50/70'}`} data-id="element-123">
        {/* Checkbox */}
        <button onClick={onToggleSelect} className="w-8 flex-shrink-0 flex justify-center focus:outline-none" data-id="element-124">
          <div className={`w-4 h-4 rounded flex items-center justify-center border transition-colors ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-slate-300 bg-white group-hover:border-blue-400'}`} data-id="element-125">
            {isSelected && <CheckIcon className="w-3 h-3 text-white" data-id="element-126" />}
          </div>
        </button>

        {/* Name & Description */}
        <div className="flex-1 min-w-0 pr-4" data-id="element-127">
          <div className="flex items-center gap-2" data-id="element-128">
            <p className={`text-sm font-semibold ${condition.status === 'cleared' ? 'text-slate-500 line-through' : 'text-slate-800'}`} data-id="element-129">
              {condition.name}
            </p>
            {history.length > 0 && <button onClick={e => {
            e.stopPropagation();
            setHistoryOpen(!historyOpen);
          }} className="w-6 h-6 rounded-full flex items-center justify-center transition-all" style={{
            backgroundColor: historyOpen ? '#dbeafe' : '#f1f5f9',
            color: historyOpen ? '#1e40af' : '#64748b',
            border: historyOpen ? '1.5px solid #93c5fd' : '1.5px solid #e2e8f0'
          }} title="View condition journey" data-id="element-130">
                <HistoryIcon className="w-3.5 h-3.5" data-id="element-131" />
              </button>}
            {comments.length > 0 && <span className="text-[10px] font-medium text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-full" data-id="element-132">
                {comments.length} comment{comments.length !== 1 ? 's' : ''}
              </span>}
          </div>
          {condition.description && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2 leading-relaxed" data-id="element-133">
              {condition.description}
            </p>}
          <div className="flex items-center gap-2 mt-1.5 flex-wrap" data-id="element-134">
            {groupName && onRemoveFromGroup && <button onClick={e => {
            e.stopPropagation();
            onRemoveFromGroup();
          }} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold transition-colors hover:opacity-80" style={{
            backgroundColor: '#dbeafe',
            color: '#1e40af',
            border: '1.5px solid #93c5fd'
          }} title="Remove from group" data-id="element-135">
                <MessageSquareIcon className="w-3.5 h-3.5" style={{
              color: '#3b82f6'
            }} data-id="element-136" />
                <span className="truncate max-w-[180px]" data-id="element-137">{groupName}</span>
                <XIcon className="w-3.5 h-3.5 ml-0.5" style={{
              color: '#60a5fa'
            }} data-id="element-138" />
              </button>}
            {linkedDocument && <div className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded text-[10px] font-medium" data-id="element-139">
                <FileTextIcon className="w-3 h-3" data-id="element-140" />
                <span className="truncate max-w-[120px]" data-id="element-141">
                  {linkedDocument.name}
                </span>
              </div>}
          </div>
        </div>

        {/* Status — clickable dropdown */}
        <div className="w-28 flex-shrink-0 flex justify-center relative" ref={dropdownRef} data-id="element-142">
          <button onClick={() => setStatusDropdownOpen(!statusDropdownOpen)} className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold uppercase tracking-wider border cursor-pointer transition-all hover:shadow-sm ${currentStatus.bg} ${currentStatus.text} ${currentStatus.border}`} title="Click to change status" data-id="element-143">
            <span data-id="element-144">{currentStatus.label}</span>
            <ChevronDownIcon className={`w-3 h-3 transition-transform ${statusDropdownOpen ? 'rotate-180' : ''}`} data-id="element-145" />
          </button>
          {statusDropdownOpen && <div className="absolute top-full mt-1 z-30 bg-white border border-slate-200 rounded-lg shadow-lg py-1 min-w-[140px] left-1/2 -translate-x-1/2" data-id="element-146">
              {ALL_STATUSES.map(status => {
            const config = STATUS_CONFIG[status];
            const isActive = condition.status === status;
            return <button key={status} onClick={() => {
              onStatusChange(status);
              setStatusDropdownOpen(false);
            }} className={`w-full text-left px-3 py-2 text-xs font-medium flex items-center gap-2.5 transition-colors ${isActive ? 'bg-slate-50' : 'hover:bg-slate-50'}`} data-id="element-147">
                    <div className={`w-2.5 h-2.5 rounded-full ${config.dot}`} data-id="element-148" />
                    <span className={`uppercase tracking-wider font-bold ${config.text}`} data-id="element-149">
                      {config.label}
                    </span>
                    {isActive && <CheckCircle2Icon className="w-3.5 h-3.5 text-slate-400 ml-auto" data-id="element-150" />}
                  </button>;
          })}
            </div>}
        </div>

        {/* Created */}
        <div className="w-24 flex-shrink-0 text-center" data-id="element-151">
          <div className="text-xs text-slate-700" data-id="element-152">
            {formatDate(condition.createdAt)}
          </div>
          <div className="text-[10px] text-slate-400" data-id="element-153">Created</div>
        </div>

        {/* Requested */}
        <div className="w-24 flex-shrink-0 text-center" data-id="element-154">
          <div className="text-xs text-slate-700" data-id="element-155">
            {formatDate(condition.requestedAt)}
          </div>
          {condition.requestedAt && <div className="text-[10px] text-slate-400" data-id="element-156">Requested</div>}
        </div>

        {/* Assigned To */}
        <div className="w-32 flex-shrink-0 flex flex-col items-center justify-center" data-id="element-157">
          {condition.assignedTo ? <>
              <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 mb-1" data-id="element-158">
                <UserIcon className="w-3.5 h-3.5" data-id="element-159" />
              </div>
              <span className="text-[10px] text-slate-500 text-center truncate w-full px-2" data-id="element-160">
                {condition.assignedTo}
              </span>
            </> : <span className="text-xs text-slate-400" data-id="element-161">-</span>}
        </div>

        {/* Actions */}
        <div className="w-56 flex-shrink-0 flex items-center justify-end gap-1.5 pr-2" data-id="element-162">
          <button onClick={() => onStatusChange('cleared')} className="w-8 h-8 rounded-full border-2 border-[#7B2D3B]/20 flex items-center justify-center text-[#7B2D3B]/70 hover:text-[#7B2D3B] hover:border-[#7B2D3B]/50 hover:bg-[#7B2D3B]/5 active:scale-95 transition-all" title="Approve / Clear" aria-label="Approve condition" data-id="element-163">
            <MailCheckIcon className="w-4 h-4" data-id="element-164" />
          </button>
          <button onClick={() => onStatusChange('new')} className="w-8 h-8 rounded-full border-2 border-[#7B2D3B]/20 flex items-center justify-center text-[#7B2D3B]/70 hover:text-[#7B2D3B] hover:border-[#7B2D3B]/50 hover:bg-[#7B2D3B]/5 active:scale-95 transition-all" title="Reject / Deny" aria-label="Reject condition" data-id="element-165">
            <MinusCircleIcon className="w-4 h-4" data-id="element-166" />
          </button>
          <div className="w-px h-5 bg-slate-200" data-id="element-167" />
          <button className="w-8 h-8 rounded-full border-2 border-[#7B2D3B]/20 flex items-center justify-center text-[#7B2D3B]/70 hover:text-[#7B2D3B] hover:border-[#7B2D3B]/50 hover:bg-[#7B2D3B]/5 active:scale-95 transition-all" title="Upload Document" aria-label="Upload document" data-id="element-168">
            <UploadIcon className="w-4 h-4" data-id="element-169" />
          </button>
          <button className="w-8 h-8 rounded-full border-2 border-[#7B2D3B]/20 flex items-center justify-center text-[#7B2D3B]/70 hover:text-[#7B2D3B] hover:border-[#7B2D3B]/50 hover:bg-[#7B2D3B]/5 active:scale-95 transition-all" title="Edit Condition" aria-label="Edit condition" data-id="element-170">
            <ClipboardPenIcon className="w-4 h-4" data-id="element-171" />
          </button>
          <button onClick={() => setCommentOpen(!commentOpen)} className={`w-8 h-8 rounded-full border-2 flex items-center justify-center active:scale-95 transition-all ${commentOpen ? 'border-blue-400 bg-blue-50 text-blue-600' : 'border-[#7B2D3B]/20 text-[#7B2D3B]/70 hover:text-[#7B2D3B] hover:border-[#7B2D3B]/50 hover:bg-[#7B2D3B]/5'}`} title="Add Comment" aria-label="Add comment" data-id="element-172">
            <MessageSquareIcon className="w-4 h-4" data-id="element-173" />
          </button>
          <button onClick={onRemove} className="w-8 h-8 rounded-full bg-[#7B2D3B] flex items-center justify-center text-white hover:bg-[#5E1F2C] active:scale-95 transition-all shadow-sm" title="Delete Condition" aria-label="Delete condition" data-id="element-174">
            <Trash2Icon className="w-4 h-4" data-id="element-175" />
          </button>
        </div>
      </motion.div>

      {/* Expandable Comments Panel */}
      <AnimatePresence data-id="element-176">
        {commentOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="overflow-hidden" data-id="element-177">
            <div className="border-b border-slate-100" style={{
          backgroundColor: '#fafbfc'
        }} data-id="element-178">
              {/* Comments header */}
              <div className="px-6 py-2.5 flex items-center gap-2 border-b border-slate-100" data-id="element-179">
                <MessageSquareIcon className="w-4 h-4" style={{
              color: '#64748b'
            }} data-id="element-180" />
                <span className="text-sm font-semibold" style={{
              color: '#334155'
            }} data-id="element-181">
                  Comments
                </span>
                {comments.length > 0 && <span className="text-[10px] font-medium text-slate-400 bg-slate-200 px-1.5 py-0.5 rounded-full" data-id="element-182">
                    {comments.length}
                  </span>}
              </div>

              {/* Existing comments */}
              {comments.length > 0 && <div className="px-6 py-2 space-y-2 max-h-48 overflow-y-auto" data-id="element-183">
                  {[...comments].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime()).map(comment => <div key={comment.id} className="flex items-start gap-2.5 py-2 px-3 rounded-lg" style={{
              backgroundColor: '#ffffff',
              border: '1px solid #e2e8f0'
            }} data-id="element-184">
                        <div className="flex-1 min-w-0" data-id="element-185">
                          <div className="flex items-center gap-2 mb-0.5" data-id="element-186">
                            <span className="text-xs font-medium text-slate-700" data-id="element-187">
                              {comment.author || 'You'}
                            </span>
                            <span className="text-[10px] text-slate-400" data-id="element-188">
                              {comment.timestamp.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit'
                    })}
                            </span>
                            <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full" style={{
                    backgroundColor: comment.visibility === 'portal' ? '#dbeafe' : '#fef3c7',
                    color: comment.visibility === 'portal' ? '#1e40af' : '#92400e'
                  }} data-id="element-189">
                              {comment.visibility === 'portal' ? <GlobeIcon className="w-2.5 h-2.5" data-id="element-190" /> : <LockIcon className="w-2.5 h-2.5" data-id="element-191" />}
                              {comment.visibility === 'portal' ? 'Portal' : 'Internal'}
                            </span>
                          </div>
                          <p className="text-sm text-slate-600" data-id="element-192">
                            {comment.text}
                          </p>
                        </div>
                      </div>)}
                </div>}

              {/* New comment input */}
              <div className="px-6 py-3" data-id="element-193">
                <textarea ref={commentInputRef} value={commentText} onChange={e => setCommentText(e.target.value)} placeholder="Type your message right here...." rows={3} className="w-full px-4 py-3 text-sm border-2 rounded-lg resize-none focus:outline-none focus:ring-0 transition-colors" style={{
              borderColor: '#7B2D3B',
              backgroundColor: '#ffffff',
              color: '#334155'
            }} onKeyDown={e => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSendComment();
            }} data-id="element-194" />
                <div className="flex items-center justify-end gap-3 mt-2" data-id="element-195">
                  {/* Portal / Internal toggle */}
                  <div className="inline-flex rounded-lg overflow-hidden" style={{
                border: '1px solid #e2e8f0'
              }} data-id="element-196">
                    <button type="button" onClick={() => setCommentVisibility('portal')} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors" style={{
                  backgroundColor: commentVisibility === 'portal' ? '#dbeafe' : '#ffffff',
                  color: commentVisibility === 'portal' ? '#1e40af' : '#64748b'
                }} data-id="element-197">
                      <GlobeIcon className="w-3.5 h-3.5" data-id="element-198" />
                      Portal
                    </button>
                    <button type="button" onClick={() => setCommentVisibility('internal')} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors" style={{
                  backgroundColor: commentVisibility === 'internal' ? '#fef3c7' : '#ffffff',
                  color: commentVisibility === 'internal' ? '#92400e' : '#64748b',
                  borderLeft: '1px solid #e2e8f0'
                }} data-id="element-199">
                      <LockIcon className="w-3.5 h-3.5" data-id="element-200" />
                      Internal
                    </button>
                  </div>

                  <button onClick={handleSendComment} disabled={!commentText.trim()} className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed" style={{
                backgroundColor: '#f1f5f9',
                color: '#475569',
                border: '1px solid #e2e8f0'
              }} data-id="element-201">
                    <SendIcon className="w-3.5 h-3.5" data-id="element-202" />
                    Send
                  </button>
                </div>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Expandable History Timeline */}
      <AnimatePresence data-id="element-203">
        {historyOpen && sortedHistory.length > 0 && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="overflow-hidden" data-id="element-204">
            <div className="px-8 py-3 border-b border-slate-100" style={{
          backgroundColor: '#fafbfc'
        }} data-id="element-205">
              <div className="flex items-center gap-1.5 mb-2" data-id="element-206">
                <HistoryIcon className="w-3 h-3" style={{
              color: '#64748b'
            }} data-id="element-207" />
                <span className="text-[11px] font-semibold" style={{
              color: '#475569'
            }} data-id="element-208">
                  Condition Journey
                </span>
              </div>
              <div className="relative ml-1" data-id="element-209">
                {/* Vertical timeline line */}
                <div className="absolute" style={{
              left: '5px',
              top: '8px',
              bottom: '8px',
              width: '1px',
              backgroundColor: '#e2e8f0'
            }} data-id="element-210" />
                {sortedHistory.map((entry, idx) => {
              const colors = getEventColor(entry.type);
              return <div key={idx} className="flex items-start gap-3 py-1.5 relative" data-id="element-211">
                      {/* Timeline dot */}
                      <div className="w-[11px] h-[11px] rounded-full flex-shrink-0 mt-0.5 flex items-center justify-center z-10" style={{
                  backgroundColor: colors.bg,
                  color: colors.text
                }} data-id="element-212">
                        {getEventIcon(entry.type)}
                      </div>
                      <div className="flex-1 min-w-0 flex items-baseline gap-2" data-id="element-213">
                        <span className="text-xs font-medium" style={{
                    color: colors.text
                  }} data-id="element-214">
                          {getEventLabel(entry)}
                        </span>
                        <span className="text-[10px] flex-shrink-0" style={{
                    color: '#94a3b8'
                  }} data-id="element-215">
                          {formatHistoryDate(entry.timestamp)}
                        </span>
                      </div>
                    </div>;
            })}
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}